import {
  Avatar,
  Box,
  Button,
  Grid,
  IconButton,
  InputLabel,
  Modal,
  Typography
} from "@mui/material";
import { default as CancelIcon, default as CloseIcon } from "@mui/icons-material/Close";
import { DataGrid, GridActionsCellItem } from "@mui/x-data-grid";
import DeleteIcon from "@mui/icons-material/DeleteOutlined";
import axios from "axios";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import React, { useEffect, useState } from "react";
import { useLocation, Link, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import FavoriteIcon from "@mui/icons-material/Favorite";
import CommentIcon from "@mui/icons-material/Comment";
import { reasons } from "./CommunityResources";

const apiBaseURL = process.env.REACT_APP_API_BASE_URL;
const appColor = "#59D6DF";

const OtherReasonModal = ({ open, onClose, details }) => {
  return (
    <Modal open={open} onClose={onClose}>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          maxHeight: "80vh",
          bgcolor: "background.paper",
          boxShadow: 24,
          borderRadius: 4,
          overflow: "hidden",
          padding: 2
        }}
      >
        <IconButton
          sx={{
            position: "absolute",
            top: 8,
            right: 8,
            color: "black"
          }}
          onClick={onClose}
        >
          <CloseIcon />
        </IconButton>

        <Box sx={{ paddingTop: 4 }}>
          <Box>{details}</Box>
        </Box>
      </Box>
    </Modal>
  );
};

const PostModal = ({ open, onClose, details }) => {
  if (!details) return null;

  return (
    <Modal open={open} onClose={onClose}>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          maxHeight: "80vh",
          width: "400px",
          bgcolor: "background.paper",
          boxShadow: 24,
          borderRadius: 4,
          overflowY: "auto",
          padding: 2
        }}
      >
        {/* User Profile Section */}
        <Box display="flex" alignItems="center" gap={2} mb={2}>
          <Avatar src={details.profilePic} alt={details.name} />
          <Box>
            <Typography fontWeight="bold">{details.name}</Typography>
            <Typography variant="body2" color="text.secondary">
              {new Date(details.createdAt).toLocaleString()}
            </Typography>
          </Box>
        </Box>

        {/* Post Content */}
        <Typography variant="body1" mb={2}>
          {details.content}
        </Typography>

        {/* Post Images */}
        {details.images?.length > 0 && (
          <Box display="flex" flexDirection="column" gap={1} mb={2}>
            {details.images.map((img, index) => (
              <img
                key={index}
                src={`${img.url}`}
                alt={`ost Image ${index}`}
                style={{ width: "100%", borderRadius: "8px" }}
              />
            ))}
          </Box>
        )}

        {/* Like & Comment Count */}
        <Box display="flex" justifyContent="space-between">
          <IconButton>
            <FavoriteIcon color="error" /> {details.likes?.length || 0}
          </IconButton>
          <IconButton>
            <CommentIcon color="primary" /> {details.comments?.length || 0}
          </IconButton>
        </Box>
      </Box>
    </Modal>
  );
};

const CommunityGuidelines = () => {
  const [rows, setRows] = useState([]);
  const [rowModesModel, setRowModesModel] = React.useState({});
  const [modalOpen, setModalOpen] = useState(false);
  const [modalPostOpen, setModalPostOpen] = useState(false);
  const [selectOtherReason, setSelectOtherReason] = useState(null);
  const [selectPost, setSelectPost] = useState(null);
  const navigate = useNavigate();

  const fetchReports = async () => {
    try {
      const headers = {
        "Content-Type": "application/json",
        "x-access-token": `${localStorage.getItem("token")}`
      };
      const reportList = await axios.get(`${apiBaseURL}/api/v1/admin/getreports`, {
        headers: headers
      });
      if (reportList?.data) {
        const { allReports } = reportList?.data.data;
        const currentRows = allReports.map((value) => ({
          id: value?._id,
          post: value?.postId ? value.postId : "N/A",
          owner: {
            profilePic: value?.postOwner?.profileImage || "",
            name: `${value?.postOwner?.firstName || ""} ${value?.postOwner?.lastName || ""}`,
            id: value?.postOwner?._id
          },
          reportedBy: {
            profilePic: value?.reportedBy?.profileImage || "",
            name: `${value?.reportedBy?.firstName || ""} ${value?.reportedBy?.lastName || ""}`,
            id: value?.reportedBy?._id
          },
          reason: reasons[`${value?.reason}`] || "N/A",
          otherReasonText: value?.otherReasonText
        }));
        setRows(currentRows);
      }
    } catch (error) {
      console.log(error, "error");
    }
  };

  const handleOpenModal = (details) => {
    setSelectOtherReason(details);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setSelectOtherReason(null);
    setModalOpen(false);
  };

  const handleOpenPostModal = (details) => {
    setSelectPost(details);
    setModalPostOpen(true);
  };

  const handleClosePostModal = () => {
    setSelectPost(null);
    setModalPostOpen(false);
  };

  useEffect(() => {
    fetchReports();
  }, []);

  const handleDeleteClick = (post) => {
    Swal.fire({
      title: "Do you want to delete this post?",
      showCancelButton: true,
      showDenyButton: true,
      confirmButtonText: "Delete & Block User",
      denyButtonText: "Delete Without Blocking",
      cancelButtonText: "Cancel",
      confirmButtonColor: appColor
    })
      .then(async (result) => {
        if (result.isConfirmed || result.isDenied) {
          const blockUser = result.isConfirmed ? 1 : 0;

          try {
            const headers = {
              "Content-Type": "application/json",
              "x-access-token": `${localStorage.getItem("token")}`
            };

            const deleteUser = await axios.delete(
              `${apiBaseURL}/api/v1/admin/deletepostandblockuser`,
              {
                headers: headers,
                data: {
                  postId: post._id,
                  userId: post.user,
                  blockUser: blockUser
                }
              }
            );

            if (deleteUser.data) {
              Swal.fire(
                "The post has been deleted!",
                blockUser ? "User has been blocked." : "User is not blocked.",
                "success"
              );
              await fetchReports();
            }
          } catch (error) {
            console.log(error, "error");
          }
        } else if (result.isDismissed) {
          Swal.fire("Action cancelled", "", "info");
        }
      })
      .catch((error) => {
        console.log(error, "error");
      });
  };

  const columns = [
    {
      field: "post",
      headerName: "Posts",
      width: 200,
      sortable: false,
      renderCell: (params) => (
        <Button
          href="#text-buttons"
          style={{ textTransform: "none" }}
          onClick={() => {
            handleOpenPostModal({ ...params.row.post, ...params.row.owner });
          }}
        >
          View Post
        </Button>
      )
    },
    {
      field: "owner",
      headerName: "Owner",
      width: 200,
      sortable: false,
      renderCell: (params) => (
        <div
          style={{ display: "flex", alignItems: "center" }}
          onClick={() => {
            navigate("/users", { state: { id: params.row.owner.id } });
          }}
        >
          <Avatar src={`${params.row.owner?.profilePic}`} alt="Owner" />
          <span style={{ marginLeft: 8 }}>
            {params.row.owner?.firstName} {params.row.owner?.lastName}
          </span>
        </div>
      )
    },
    {
      field: "reportedBy",
      headerName: "Reported By",
      width: 200,
      sortable: false,
      renderCell: (params) => (
        <div
          style={{ display: "flex", alignItems: "center" }}
          onClick={() => {
            navigate("/users", { state: { id: params.row.reportedBy.id } });
          }}
        >
          <Avatar src={`${params.row.reportedBy?.profilePic}`} alt="Reporter" />
          <span style={{ marginLeft: 8 }}>
            {params.row.reportedBy?.firstName} {params.row.reportedBy?.lastName}
          </span>
        </div>
      )
    },
    { field: "reason", headerName: "Reason", width: 200, sortable: false },
    {
      field: "otherReasonText",
      headerName: "Other Reason",
      sortable: false,
      width: 200,
      renderCell: (params) => (
        <Button
          href="#text-buttons"
          style={{ textTransform: "none" }}
          onClick={() => {
            if (params.row?.otherReasonText) {
              handleOpenModal(params.row.otherReasonText);
            }
          }}
        >
          {params.row.otherReasonText ? "View Other Reason" : "N/A"}
        </Button>
      )
    },
    {
      field: "actions",
      type: "actions",
      headerName: "Actions",
      sortable: false,
      width: 150,
      cellClassName: "actions",
      getActions: (params) => {
        return [
          <GridActionsCellItem
            icon={<DeleteIcon />}
            label="Delete"
            onClick={() => {
              handleDeleteClick(params.row.post);
            }}
            color="inherit"
          />
        ];
      }
    }
  ];

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Box
        sx={{
          height: 675,
          width: "100%",
          "& .actions": {
            color: "text.secondary"
          },
          "& .textPrimary": {
            color: "text.primary"
          }
        }}
      >
        <DataGrid
          rows={rows}
          columns={columns}
          disableColumnResize
          disableColumnMenu
          editMode="row"
          rowModesModel={rowModesModel}
          initialState={{
            pagination: { paginationModel: { pageSize: 10 } }
          }}
          pageSizeOptions={rows.length ? [10, 20, 30] : []}
          sx={{
            fontFamily: `"Roboto","Helvetica","Arial",sans-serif`,
            fontWeight: 400,
            "& .MuiDataGrid-columnHeader": {
              borderRight: "1px solid rgba(224, 224, 224, 1)"
            },
            "& .MuiDataGrid-cell": {
              borderRight: "1px solid rgba(224, 224, 224, 1)"
            },
            "& .MuiDataGrid-row": {
              backgroundColor: "#fff",
              "&:nth-of-type(odd)": {
                backgroundColor: "#f9f9f9"
              }
            },
            "& .MuiDataGrid-cell:focus, & .MuiDataGrid-row:focus": {
              outline: "none"
            },
            "& .MuiDataGrid-cell:focus-within": {
              outline: "none"
            },
            "& .Mui-selected": {
              backgroundColor: "inherit !important"
            },
            "& .MuiDataGrid-columnHeader": {
              "& .MuiDataGrid-iconButtonContainer": {
                display: "none"
              },
              "& .MuiDataGrid-menuIcon": {
                display: "none"
              }
            }
          }}
          localeText={{ noRowsLabel: "No Post is available" }}
          slotProps={{
            toolbar: { setRows }
          }}
        />
        <OtherReasonModal open={modalOpen} onClose={handleCloseModal} details={selectOtherReason} />
        <PostModal open={modalPostOpen} onClose={handleClosePostModal} details={selectPost} />
      </Box>
    </DashboardLayout>
  );
};

export default CommunityGuidelines;
